/**
 * UndoRedoControls - Controles flotantes para Undo/Redo
 *
 * @version 1.0
 * @updated 2026-02-19
 * @module core/map-tools
 *
 * @description
 * Muestra botones flotantes para deshacer/rehacer acciones.
 * Solo visible en modo EDITAR cuando hay operaciones disponibles.
 *
 * @remarks
 * - Posición: fixed, derecha inferior (sobre el mapa)
 * - Solo visible cuando `mode === 'inventory'`
 * - Botones deshabilitados cuando no hay operaciones
 * - Tooltips con descripción de la operación
 */

import { Undo2, Redo2 } from 'lucide-react';
import { useOperationalModeStore } from '../operational-modes';
import { useUndoRedo } from './useUndoRedo';
import './undo-redo-controls.css';

/**
 * Componente principal
 */
export function UndoRedoControls(): JSX.Element | null {
  const { mode } = useOperationalModeStore();
  const { canUndo, canRedo, undo, redo, undoDescription, redoDescription } = useUndoRedo();

  // Solo mostrar en modo edición
  if (mode !== 'inventory') {
    return null;
  }

  // No mostrar si no hay operaciones disponibles
  if (!canUndo && !canRedo) {
    return null;
  }

  return (
    <div className="undo-redo-controls">
      {/* Botón Deshacer */}
      <button
        className="undo-redo-btn"
        disabled={!canUndo}
        onClick={undo}
        title={canUndo ? `Deshacer: ${undoDescription} (Ctrl+Z)` : 'No hay acciones para deshacer'}
        aria-label="Deshacer"
      >
        <Undo2 size={18} />
      </button>

      {/* Botón Rehacer */}
      <button
        className="undo-redo-btn"
        disabled={!canRedo}
        onClick={redo}
        title={canRedo ? `Rehacer: ${redoDescription} (Ctrl+Y)` : 'No hay acciones para rehacer'}
        aria-label="Rehacer"
      >
        <Redo2 size={18} />
      </button>
    </div>
  );
}
